import { Injectable } from '@angular/core';
import { openDB, IDBPDatabase } from 'idb';
import * as Sentry from '@sentry/angular';

// Module level so every TidyStorage instance shares it: the class is both providedIn root and
// listed in the providers aggregator.
const pendingUpdates = new Map<string, Promise<unknown>>();

@Injectable({
  providedIn: 'root',
})
export class TidyStorage {
  // While IndexedDB can't be opened, values live in memory for the session and are mirrored to
  // localStorage, so they survive a relaunch. Deletes and clear() are recorded too (in memory as
  // well, in case localStorage throws), so they reach IndexedDB when it opens again instead of old
  // values coming back.
  private static readonly FALLBACK_PREFIX = 'tidyStorageFallback:';
  private static readonly DELETED_PREFIX = 'tidyStorageFallbackDeleted:';
  private static readonly CLEARED_KEY = 'tidyStorageFallbackCleared';
  private static readonly OPENED_BEFORE_KEY = 'tidyStorageOpenedIndexedDB';
  // Bigger values (to-dos holding base64 photos) stay in memory only, so the mirror can't fill
  // localStorage and break the app's own localStorage writes.
  private static readonly MAX_MIRRORED_LENGTH = 1000000;
  private static readonly OPEN_ATTEMPTS = 3;

  private db: IDBPDatabase | null = null;
  private fallback: Map<string, any> | null = null;
  private fallbackDeleted = new Set<string>();
  private fallbackCleared = false;
  private openedIndexedDB = false;
  private isInitializing = false;

  constructor() {}

  async init() {
    if (this.isInitializing) {
      // Wait for ongoing initialization to complete
      while (this.isInitializing) {
        await new Promise((resolve) => setTimeout(resolve, 10));
      }
      return;
    }

    this.isInitializing = true;
    try {
      const db = await this.openIndexedDB();
      await this.moveFallbackIntoDb(db);
      this.db = db;
      this.fallback = null;
      this.openedIndexedDB = true;
      this.setLocal(TidyStorage.OPENED_BEFORE_KEY, '1');
    } catch (error) {
      // Once IndexedDB has worked this session, a failed reopen is a real error: switching to
      // the (empty) fallback would make a job in progress look like none.
      if (this.openedIndexedDB) {
        throw error;
      }
      // Some iOS WebViews start without IndexedDB ("Can't find variable: indexedDB"). Startup
      // awaits init, so throwing here left the app on the splash screen. A reconnect that fails
      // again keeps this session's in-memory values.
      if (!this.fallback) {
        console.warn('TidyStorage: IndexedDB unavailable, using fallback storage', error);
        this.reportFallback(error);
        this.db = null;
        this.useFallback();
      }
    } finally {
      this.isInitializing = false;
    }
  }

  // Waits for a running init too: on a resume it moves the fallback into IndexedDB, and a save that
  // went into the fallback meanwhile would be dropped with it.
  private async ensureConnection(): Promise<void> {
    if (this.isInitializing || (!this.db && !this.fallback)) {
      await this.init();
    }
  }

  private async openIndexedDB(): Promise<IDBPDatabase> {
    let lastError;
    for (let attempt = 1; attempt <= TidyStorage.OPEN_ATTEMPTS; attempt++) {
      try {
        return await openDB('TidyStorageDB', 1, {
          upgrade(db) {
            db.createObjectStore('keyvaluepairs');
          },
        });
      } catch (error) {
        lastError = error;
        if (attempt < TidyStorage.OPEN_ATTEMPTS) {
          await new Promise((resolve) => setTimeout(resolve, 300));
        }
      }
    }
    throw lastError;
  }

  // console.warn is only a breadcrumb; report once so a quiet PRO-APP-S8 reads as "handled", not "gone".
  private reportFallback(error) {
    Sentry.captureMessage('TidyStorage fallback: IndexedDB unavailable', {
      level: 'warning',
      tags: { indexeddb_opened_on_earlier_launch: this.getLocal(TidyStorage.OPENED_BEFORE_KEY) === '1' },
      extra: { error: String(error?.message || error) },
    });
  }

  private useFallback() {
    this.fallback = new Map();
    for (const storageKey of this.localKeys(TidyStorage.FALLBACK_PREFIX)) {
      try {
        const key = storageKey.slice(TidyStorage.FALLBACK_PREFIX.length);
        this.fallback.set(key, JSON.parse(localStorage.getItem(storageKey)));
      } catch {
        // Skip an entry that can't be read.
      }
    }
  }

  private writeFallback(key: string, value: any) {
    const mirrorKey = TidyStorage.FALLBACK_PREFIX + key;
    const deletedKey = TidyStorage.DELETED_PREFIX + key;
    if (value === undefined) {
      this.fallback.delete(key);
      this.removeLocal(mirrorKey);
      this.fallbackDeleted.add(key);
      this.setLocal(deletedKey, '1');
      return;
    }
    // A later save keeps the delete mark: IndexedDB's copy still has to go before this value moves in.
    this.fallback.set(key, structuredClone(value));
    const json = JSON.stringify(value);
    if (json === undefined || json.length > TidyStorage.MAX_MIRRORED_LENGTH || !this.setLocal(mirrorKey, json)) {
      // Kept in memory only. Drop the older mirrored copy so it can't come back in its place.
      this.removeLocal(mirrorKey);
    }
  }

  private clearFallback() {
    this.fallback.clear();
    this.fallbackDeleted.clear();
    this.fallbackCleared = true;
    try {
      localStorage.clear();
    } catch {
      // No localStorage.
    }
    this.setLocal(TidyStorage.CLEARED_KEY, '1');
  }

  // Deletes and clear() made while IndexedDB was unavailable are applied, then the saved values
  // move in, then the records are dropped so a later launch can't replay them.
  private async moveFallbackIntoDb(db: IDBPDatabase) {
    const cleared = this.fallbackCleared || this.getLocal(TidyStorage.CLEARED_KEY) === '1';
    const deleted = new Set([
      ...this.fallbackDeleted,
      ...this.localKeys(TidyStorage.DELETED_PREFIX).map((key) => key.slice(TidyStorage.DELETED_PREFIX.length)),
    ]);
    // This session's values (some may be memory-only) replace what IndexedDB holds: they're on screen,
    // and the app goes on reading and writing from them. A previous launch's values, read from the
    // mirror, were written without seeing IndexedDB (that launch started empty), so they only fill
    // keys it doesn't hold: the progress from before that launch comes back. After a clear() (log
    // out or log in), IndexedDB is empty and they all move in.
    const fromThisSession = !!this.fallback;
    const values: [string, any][] = this.fallback
      ? [...this.fallback.entries()]
      : this.localKeys(TidyStorage.FALLBACK_PREFIX).map((storageKey) => {
          try {
            return [storageKey.slice(TidyStorage.FALLBACK_PREFIX.length), JSON.parse(localStorage.getItem(storageKey))];
          } catch {
            return null;
          }
        }).filter(Boolean) as [string, any][];
    if (!cleared && !deleted.size && !values.length) {
      return;
    }
    try {
      if (cleared) {
        await db.clear('keyvaluepairs');
      }
      for (const key of deleted) {
        await db.delete('keyvaluepairs', key);
      }
      for (const [key, value] of values) {
        if (!fromThisSession && !cleared && (await db.getKey('keyvaluepairs', key)) !== undefined) {
          continue;
        }
        await db.put('keyvaluepairs', value, key);
      }
    } catch (error) {
      console.warn('TidyStorage: could not move fallback values into IndexedDB', error);
    }
    this.fallbackDeleted.clear();
    this.fallbackCleared = false;
    this.removeLocal(TidyStorage.CLEARED_KEY);
    [...this.localKeys(TidyStorage.DELETED_PREFIX), ...this.localKeys(TidyStorage.FALLBACK_PREFIX)]
      .forEach((key) => this.removeLocal(key));
  }

  private localKeys(prefix: string): string[] {
    try {
      return Object.keys(localStorage).filter((key) => key.startsWith(prefix));
    } catch {
      return [];
    }
  }

  private getLocal(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private setLocal(key: string, value: string): boolean {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch {
      return false;
    }
  }

  private removeLocal(key: string) {
    try {
      localStorage.removeItem(key);
    } catch {
      // No localStorage.
    }
  }

  private async executeWithRetry<T>(operation: () => Promise<T>): Promise<T> {
    const maxRetries = 3;
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        await this.ensureConnection();
        return await operation();
      } catch (error) {
        lastError = error as Error;

        // If it's a database connection error, try to reinitialize
        if (
          error instanceof Error &&
          (error.message.includes('database connection is closing') ||
            error.message.includes('Database not initialized') ||
            error.message.includes('The database connection is closing') ||
            error.message.includes('failed to execute') ||
            error.message.includes('transaction'))
        ) {
          console.warn(
            `Database connection error detected, attempting retry ${
              attempt + 1
            }/${maxRetries}:`,
            error.message
          );

          // Reset the database connection
          this.db = null;

          // Wait a bit before retrying
          await new Promise((resolve) =>
            setTimeout(resolve, 100 * (attempt + 1))
          );
          continue;
        }

        // For other errors, don't retry
        throw error;
      }
    }

    console.error('Database operation failed after all retries:', lastError);
    throw lastError || new Error('Database operation failed after retries');
  }

  async save(key: string, value: any): Promise<void> {
    return this.executeWithRetry(async () => {
      if (this.fallback) return this.writeFallback(key, value);
      if (!this.db) throw new Error('Database not initialized');
      await this.db.put('keyvaluepairs', value, key);
    });
  }

  async retrieve(key: string): Promise<any> {
    try {
      return await this.executeWithRetry(async () => {
        if (this.fallback) return this.fallback.has(key) ? structuredClone(this.fallback.get(key)) : undefined;
        if (!this.db) throw new Error('Database not initialized');
        return await this.db.get('keyvaluepairs', key);
      });
    } catch (error) {
      if (
        error instanceof Error &&
        error.message.includes('Database not initialized')
      ) {
        console.warn(
          `TidyStorage.retrieve('${key}'): database unavailable, returning null`
        );
        return null;
      }
      throw error;
    }
  }

  /**
   * Read-modify-write of one key, one at a time per key, so two writers can't save over each
   * other's changes with a stale copy. Keep the callback local (no network calls): every other
   * update of the key waits for it. Returning undefined leaves the key as it is.
   */
  async update<T = any>(key: string, mutate: (value: T) => T | Promise<T>): Promise<T> {
    const previous = pendingUpdates.get(key) || Promise.resolve();
    const current = previous.catch(() => undefined).then(async () => {
      const value = await mutate(await this.retrieve(key));
      if (value !== undefined) {
        await this.save(key, value);
      }
      return value;
    });
    pendingUpdates.set(key, current);
    try {
      return await current;
    } finally {
      if (pendingUpdates.get(key) === current) {
        pendingUpdates.delete(key);
      }
    }
  }

  async delete(key: string): Promise<void> {
    return this.executeWithRetry(async () => {
      if (this.fallback) return this.writeFallback(key, undefined);
      if (!this.db) throw new Error('Database not initialized');
      await this.db.delete('keyvaluepairs', key);
    });
  }

  async clear(): Promise<void> {
    return this.executeWithRetry(async () => {
      if (this.fallback) {
        return this.clearFallback();
      }
      if (!this.db) throw new Error('Database not initialized');
      await this.db.clear('keyvaluepairs');
      localStorage.clear();
    });
  }

  /**
   * Force reconnection to the database
   * Useful when the app comes back from background on iOS
   */
  async forceReconnect(): Promise<void> {
    console.log('Forcing database reconnection...');
    // On the fallback, keep this session's values: init moves them in if IndexedDB opens now.
    if (!this.fallback) {
      this.db = null;
    }
    await this.init();
    console.log('Database reconnection completed');
  }
}
